import React from 'react';
import {
  TrendingUp,
  ShoppingCart,
  Target,
  PieChart,
  Calendar,
  ArrowUpRight,
  Sparkles,
  Info,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Gürgençler — CX Geçişi Sonrası YoY Rapor
//
// Karşılaştırma:
//   • Önceki dönem (CX Öncesi): Ekim 2024 – Mart 2025
//   • Yeni dönem (CX Sonrası):  Ekim 2025 – Mart 2026
//
// Kaynak: GA4. Rakamlar manuel hesaplanıp aşağıdaki sabitlere yazılıyor.
// Dönem sonu / yeni veri çekildikçe `metrics` ve `headline` güncellenir.
// ---------------------------------------------------------------------------

const PERIOD_PREV_LABEL = 'Ekim 2024 – Mart 2025';
const PERIOD_CURR_LABEL = 'Ekim 2025 – Mart 2026';

const metrics = [
  {
    key: 'traffic',
    label: 'Trafik (YoY)',
    icon: TrendingUp,
    accent: 'emerald',
    yoyPct: 22.8,
    direction: 'up',
    headlineValue: '+22,8%',
    detail: 'Oturum / kullanıcı bazında trafik artışı',
    source: 'GA4 — Sessions',
  },
  {
    key: 'addtocart',
    label: 'Add to Cart (YoY)',
    icon: ShoppingCart,
    accent: 'sky',
    yoyPct: 156,
    direction: 'up',
    headlineValue: '+156%',
    secondary: '359K',
    secondaryLabel: 'Yeni dönem hacmi',
    detail: 'Sepete ekleme olay sayısı',
    source: 'GA4 — add_to_cart event',
  },
  {
    key: 'conversion',
    label: 'Conversion Rate (YoY)',
    icon: Target,
    accent: 'violet',
    yoyPct: 32.9,
    direction: 'up',
    headlineValue: '+32,9%',
    secondary: '+0,3 pp',
    secondaryLabel: 'Mutlak puan artışı',
    detail: 'Site genel dönüşüm oranı',
    source: 'GA4 — Conversions / Sessions',
  },
  {
    key: 'sob',
    label: 'Digital SOB',
    icon: PieChart,
    accent: 'amber',
    yoyPct: null,
    direction: null,
    headlineValue: '—',
    detail: 'Digital Share of Business — veri çekildiğinde güncellenecek',
    source: 'GA4 + offline satış (TBD)',
    pending: true,
  },
];

// Tailwind cannot infer dynamic class names, so explicit lookup tables are used.
const ACCENT_RING = {
  emerald: 'ring-emerald-200/70 from-emerald-50 to-white',
  sky: 'ring-sky-200/70 from-sky-50 to-white',
  violet: 'ring-violet-200/70 from-violet-50 to-white',
  amber: 'ring-amber-200/70 from-amber-50 to-white',
};
const ACCENT_TEXT = {
  emerald: 'text-emerald-700',
  sky: 'text-sky-700',
  violet: 'text-violet-700',
  amber: 'text-amber-700',
};
const ACCENT_BG = {
  emerald: 'bg-emerald-100 text-emerald-700',
  sky: 'bg-sky-100 text-sky-700',
  violet: 'bg-violet-100 text-violet-700',
  amber: 'bg-amber-100 text-amber-700',
};

function MetricCard({ metric }) {
  const Icon = metric.icon;
  const ring = ACCENT_RING[metric.accent] || ACCENT_RING.emerald;
  const txt = ACCENT_TEXT[metric.accent] || ACCENT_TEXT.emerald;
  const badgeBg = ACCENT_BG[metric.accent] || ACCENT_BG.emerald;

  return (
    <div className={`relative bg-gradient-to-br ${ring} ring-1 rounded-2xl p-6 shadow-sm`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium ${badgeBg}`}>
          <Icon className="w-3.5 h-3.5" />
          {metric.label}
        </div>
        {metric.direction === 'up' && !metric.pending && (
          <ArrowUpRight className={`w-5 h-5 ${txt}`} />
        )}
      </div>

      <div className="flex items-baseline gap-3">
        <span className={`text-4xl font-bold tracking-tight ${metric.pending ? 'text-gray-400' : txt}`}>
          {metric.headlineValue}
        </span>
        {metric.secondary && (
          <span className="text-sm font-medium text-gray-600">
            {metric.secondary}
            {metric.secondaryLabel && (
              <span className="block text-xs text-gray-400 font-normal">{metric.secondaryLabel}</span>
            )}
          </span>
        )}
      </div>

      <p className="mt-3 text-sm text-gray-700 leading-snug">{metric.detail}</p>
      <p className="mt-2 text-xs text-gray-400">{metric.source}</p>

      {metric.pending && (
        <span className="absolute top-4 right-4 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
          Bekleniyor
        </span>
      )}
    </div>
  );
}

function PeriodPill({ label, sublabel, tone = 'gray' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700 ring-gray-200',
    emerald: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  };
  return (
    <div className={`inline-flex flex-col items-start px-4 py-2.5 rounded-xl ring-1 ${tones[tone]}`}>
      <span className="text-[11px] uppercase tracking-wider opacity-70">{sublabel}</span>
      <span className="text-sm font-semibold">{label}</span>
    </div>
  );
}

export default function GurgenclerCXReport() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold ring-1 ring-emerald-200 mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Gürgençler · CX Geçişi Sonrası Rapor
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
            CX Yenilenmesi Sonrası YoY Performans
          </h1>
          <p className="mt-3 text-gray-600 max-w-3xl leading-relaxed">
            Ekim 2025'te yeni tasarım kimliğine geçen <strong>gurgencler.com.tr</strong>'nin
            CX dönüşümü sonrası ilk altı aylık (Ekim – Mart) periyotta gösterdiği YoY
            ilerleme. Karşılaştırma, geçiş öncesi aynı altı aylık dönemle yapılmıştır.
          </p>
        </div>

        {/* Period bar */}
        <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-5 mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-3">
            <Calendar className="w-4 h-4" />
            Karşılaştırma Pencereleri
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
            <PeriodPill sublabel="CX Öncesi" label={PERIOD_PREV_LABEL} tone="gray" />
            <ArrowUpRight className="w-5 h-5 text-gray-300 hidden sm:block rotate-45" />
            <PeriodPill sublabel="CX Sonrası" label={PERIOD_CURR_LABEL} tone="emerald" />
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {metrics.map((m) => (
            <MetricCard key={m.key} metric={m} />
          ))}
        </div>

        {/* Insight callout */}
        <div className="mt-10 bg-white ring-1 ring-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Özet</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                CX geçişi sonrası ilk altı ayda site trafiği <strong>%22,8</strong>,
                sepete ekleme <strong>%156</strong> (359K hacim), genel dönüşüm oranı
                ise <strong>%32,9</strong> (mutlak +0,3 puan) artış göstermiştir.
                Dijital SOB metriği veri tamamlandığında bu rapora eklenecektir.
              </p>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-6 flex items-start gap-2 text-xs text-gray-500">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            Tüm metrikler GA4'ten çekilmiştir. CX dönüşümü 2025 Ekim ayında
            tamamlanmıştır; karşılaştırılan iki pencere aynı takvim aylarını
            (Ekim–Mart) kapsar, mevsimsellik etkisi minimize edilmiştir.
          </p>
        </div>
      </div>
    </div>
  );
}
